import { Box, Button, Grid, Typography, useTheme } from "@mui/material"
import { hasRole } from "auth/hasRole"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import ContinueDialog from "components/Dialogs/ContinueDialog"
import { allRoles } from "generated/roles"
import { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CheckTemplatesTabProps } from "./CheckTemplates.interfaces"
import { CheckTemplateIntervalType } from "../../types/CheckTypes.interfaces"
import { showDate } from "components/DateHandler/dateHandler"
import { StyledHeaderContainer, StyledTableContainer } from "shared/styled/Global"
import { DynamicTooltip } from "components/Tooltip/DynamicTooltip"
import { includesPrimitiveFilter } from "components/ClpTable/components/helpers/includesFilterFn"

const checkTemplateTableDesc = `Inspection templates are used to <strong>plan and automate recurring inspections</strong> of vehicles.
You decide how the next inspection should be calculated based on <strong>date, mileage</strong>, or <strong>inspection interval</strong>. Date- and mileage-based inspections can be combined, while inspection-interval–based ones are used separately.<br />

In the next step, you select <strong>which vehicles or vehicle groups</strong> to include and enter the <strong>latest known inspection values</strong>, such as the most recent service date or odometer reading.
Based on this information, the next scheduled inspection is calculated automatically.`

const deleteCheckTemplateText = `Are you sure you want to delete the inspection template <strong>{name}</strong>?
<br />
The template contains <strong>{count} vehicles</strong>.
All services linked to the template will be deleted.`

const CheckTemplatesTab = ({
  onCreateTemplate,
  onEditTemplate,
  onDeleteTemplate,
  templatesListResult,
  deleteTemplateResult,
}: CheckTemplatesTabProps) => {
  const appContext = useContext(AppContext)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<any>(null)
  const { t } = useTranslation()

  const columns = useMemo(() => {
    const result: any[] = [
      {
        accessorKey: "name",
        header: t("global.name", "Name")
      },
      {
        id: "type",
        header: t("global.type", "Type"),
        accessorFn: (row: any) => {
          const intervals = row.checkTemplateIntervals ?? []
          const types = intervals.map((interval: any) => {
            if (interval.sourceType === 4) return t("global.inspection", "Inspection")

            const baseType =
              interval.type === 1
                ? t("global.date_based", "Date based")
                : t("global.distance_based", "Distance based")

            const calculationType =
              interval.calculationType === 1
                ? ` (${t("global.dynamic", "Dynamic")})`
                : interval.calculationType === 2
                  ? ` (${t("global.static", "Static")})`
                  : ""

            return baseType + calculationType
          })

          return types.join(", ")
        },
        cell: ({ getValue }: any) => getValue(),
      },
      {
        id: "interval",
        header: t("global.interval", "Interval"),

        accessorFn: (row: any) => {
          const intervals = row.checkTemplateIntervals ?? []

          return intervals
            .map((interval: any) => {
              if (interval.sourceType === 4) return t("global.inspection", "Inspection")

              const value = interval.value
              const unit =
                interval.type === CheckTemplateIntervalType.Days
                  ? ` ${t("global.days", "days")}`
                  : " km"

              return value ? `${value}${unit}` : ""
            })
            .filter(Boolean)
            .join(", ")
        },

        cell: (info: any) => info.getValue(),
      },
      {
        accessorFn: (row: any) => row.vehicleCount,
        header: t("global.vehicle_count", "Vehicle count"),
        filterFn: (row: any, columnId: any, filterValue: any) => includesPrimitiveFilter({ row, columnId, filterValue }),
      },
      {
        accessorKey: "updatedTime",
        header: t("global.last_updated", "Last updated"),
        cell: ({ row }: any) => {
          const date = row.original.updatedTime || row.original.createdTime
          return showDate(date)
        }
      }
    ]
    if (hasRole(appContext.appUser, allRoles.VehicleRegistryAdmin)) {
      result.push({
        accessorKey: "actions",
        numeric: false,
        disablePadding: true,
        header: "",
        align: "right",
        cell: ({ row }: any) => (
          <StyledTableButtonGroup>
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => onEditTemplate(row.original)}
            />
            <TableButton
              type={TableButtonTypeEnum.Delete}
              handleOnClick={() => {
                setTemplateToDelete(row.original)
                setDeleteDialogOpen(true)
              }}
              disabled={!row.original.isDeletable}
              toolTipText={!row.original.isDeletable ? t("checks.cannot_delete_template_with_history", "Cannot delete template with existing check history") : undefined}
            />
          </StyledTableButtonGroup>
        )
      })
    }
    return result
  }, [appContext.appUser, onEditTemplate])

  const handleDeleteConfirm = () => {
    if (!templateToDelete) return
    onDeleteTemplate(templateToDelete.id)
  }

  useEffect(() => {
    if (deleteTemplateResult.status === 2) {
      setTemplateToDelete(null)
    }
  }, [deleteTemplateResult])

  return (
    <>

      <StyledHeaderContainer>
        <Box>
          <Typography variant="h1">{t("global.check_templates", "Check templates")}</Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={onCreateTemplate}
        >
          {t("global.create_template", "Create template")}
        </Button>
      </StyledHeaderContainer>

      <StyledTableContainer>
        <Box display="flex" alignItems="center" gap={1} px={{ xs: 2, sm: 3 }} pt={3}>
          <Typography variant="labelSmall">{t("global.information", "Information")}</Typography>
          <DynamicTooltip t={t} title={t("global.check_templates", "Check templates")}>
            <Grid item>
              <Typography fontWeight="600" variant="labelSmall">
                {t("vehicleregistry.how_to_use_check_templates", "How to use control templates")}
              </Typography>
              <Typography
                sx={{ mt: 0.5 }}
                variant="bodySmall"
                color="textSecondary"
                dangerouslySetInnerHTML={{
                  __html: t("checks.check_template_table_desc", checkTemplateTableDesc)
                }}
              />
            </Grid>
          </DynamicTooltip>
        </Box>
        <ClpTable
          columns={columns}
          rows={templatesListResult.status === 2 && templatesListResult.data}
          loadingState={templatesListResult.status}
          noRowsErrorText={t("checks.checktemplates_norows", "No check templates has been created")}
        />
      </StyledTableContainer>


      {deleteDialogOpen && templateToDelete && (
        <ContinueDialog
          onAddSubmit={handleDeleteConfirm}
          setShowContinueModal={setDeleteDialogOpen}
          result={deleteTemplateResult}
          cancelBtnText={t("global.buttons.cancel", "Cancel")}
          continueBtnText={t("global.delete", "Delete")}
          isOpen={deleteDialogOpen}
        >
          <Typography align="center" variant="h4" paragraph>
            {t("checks.delete_check_template", "Delete check template")}
          </Typography>
          <Typography
            sx={{ mb: 0 }}
            align="center"
            paragraph
            dangerouslySetInnerHTML={{
              __html: t("checks.template_table_desc", deleteCheckTemplateText).replace("{name}", templateToDelete?.name).replace("{count}", templateToDelete?.vehicleCount || 0)
            }}
          />
        </ContinueDialog>
      )}
    </>
  )
}

export default CheckTemplatesTab
